import { useCallback, useEffect, useRef } from 'react';
import { ArrowLeft, ArrowUp } from 'shared/icons';
import { DataTable } from './DataTable';
import { DetailPanel } from './DetailPanel';
import { Splitter } from './Splitter';
import { MIN_DETAIL_PX, useSplitRatio } from './splitSettings';
import { useTokens } from './tokens';

function EmptyDetail({ orientation }) {
    const T = useTokens();
    // Sem `flex: 1` ou border próprio — quem controla o tamanho é o pai (Workspace).
    // Aqui só ocupamos o espaço alocado e mostramos o placeholder centrado.
    const ArrowIcon = orientation === 'vertical' ? ArrowLeft : ArrowUp;
    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                display: 'grid',
                placeItems: 'center',
                color: T.muted,
                fontSize: 13,
                fontFamily: T.sans,
                background: T.bgSoft,
            }}
        >
            <div style={{ textAlign: 'center', lineHeight: 2 }}>
                <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center' }}>
                    <ArrowIcon size={32} color={T.subtle} />
                </div>
                Selecione um produto
            </div>
        </div>
    );
}

// SplitLayout removido — toda lógica de layout agora vive inline em Workspace
// pra manter o DataTable na MESMA posição React entre todos os modos
// (preserva scroll horizontal/vertical sem precisar de localStorage).

export function Workspace({
    layoutMode,
    preset,
    filteredRows,
    selected,
    onSelect,
    onAddCart,
    sortCol,
    sortDir,
    onSort,
    periodDays,
    ordersCount,
    ordersTruncated = false,
    onUpdateRow,
    cartBySku,
    onSetCartQty,
    warehouseNameById,
    builderNode,
    editing,
    onReorderColumns,
    customColumnsById,
    allMlbRows,
    presetIdentifier,
    periodStartIso = null,
    periodEndIso = null,
}) {
    const T = useTokens();
    // Em modo MLB row.sku pode ser nulo, então comparamos por row.id (== sku ou == mlb).
    const selectedKey = selected?.id || selected?.sku;
    const selectedIdx = filteredRows.findIndex((r) => (r.id || r.sku) === selectedKey);
    const goPrev = useCallback(
        () => selectedIdx > 0 && onSelect(filteredRows[selectedIdx - 1]),
        [selectedIdx, filteredRows, onSelect],
    );
    const goNext = useCallback(
        () => selectedIdx < filteredRows.length - 1 && onSelect(filteredRows[selectedIdx + 1]),
        [selectedIdx, filteredRows, onSelect],
    );

    useEffect(() => {
        if (layoutMode !== 'modal' || !selected) {
            return undefined;
        }
        const h = (e) => {
            if (e.key === 'Escape') {
                onSelect(null);
            }
        };
        document.addEventListener('keydown', h);
        return () => document.removeEventListener('keydown', h);
    }, [layoutMode, selected, onSelect]);

    const { ratio: verticalRatio, setRatio: setVerticalRatio } = useSplitRatio('vertical');
    const { ratio: horizontalRatio, setRatio: setHorizontalRatio } = useSplitRatio('horizontal');
    // Drawer (overlay) também usa ratio persistido — antes era largura fixa
    // 460px o que cortava abas/KPIs em viewports largos e ficava espremido em
    // estreitos. Agora segue a mesma config do split vertical.
    const { ratio: overlayRatio } = useSplitRatio('overlay');

    const tableProps = {
        preset,
        rows: filteredRows,
        selectedId: selectedKey,
        onSelect,
        onAddCart,
        sortCol,
        sortDir,
        onSort,
        periodDays,
        ordersCount,
        ordersTruncated,
        cartBySku,
        onSetCartQty,
        editing,
        onReorderColumns,
        customColumnsById,
    };
    const detailProps = {
        row: selected,
        onClose: () => onSelect(null),
        onAddCart,
        hasNav: true,
        onPrev: goPrev,
        onNext: goNext,
        onUpdateRow,
        warehouseNameById,
        // Lista RAW de mlbRows + identifier do preset ativo — DetailPanel usa
        // pra renderizar breakdown por anúncio na aba Vendas (modo SKU mostra
        // todos os MLBs do SKU; modo MLB mostra só o atual).
        allMlbRows,
        presetIdentifier,
        // Range UTC ISO do período ativo — Sales tab clipa o LineChart aqui.
        periodStartIso,
        periodEndIso,
    };

    // ESTRUTURA UNIFICADA: DataTable vive numa ÚNICA posição React.
    // Trocar de layoutMode só muda flexDirection, ratio, ou adiciona/remove
    // overlays — o componente DataTable NUNCA desmonta entre modos, então
    // scroll horizontal/vertical é preservado naturalmente.
    //
    // Modos:
    //   - vertical:    SplitLayout flex-row, table à esquerda, detail à direita
    //   - horizontal:  SplitLayout flex-column, table em cima, detail embaixo
    //   - modal:       table full + DetailPanel popup centrado
    //   - list:        table full + DetailPanel fullscreen overlay
    //   - overlay:     table full + DetailPanel drawer lateral
    //   - builder open: força vertical 50/50 com builderNode no detail
    const builderActive = Boolean(builderNode);
    const effectiveMode = builderActive ? 'vertical' : layoutMode;
    const isVerticalSplit = effectiveMode === 'vertical';
    const isHorizontalSplit = effectiveMode === 'horizontal';
    const isOverlayLike = effectiveMode === 'modal' || effectiveMode === 'list' || effectiveMode === 'overlay';
    const isSplit = isVerticalSplit || isHorizontalSplit;

    const effectiveRatio = builderActive ? 50 : isVerticalSplit ? verticalRatio : horizontalRatio;
    const onRatioChange = builderActive ? () => {} : isVerticalSplit ? setVerticalRatio : setHorizontalRatio;
    const detailContent = builderActive ? (
        builderNode
    ) : selected ? (
        <DetailPanel {...detailProps} />
    ) : (
        <EmptyDetail orientation={isVerticalSplit ? 'vertical' : 'horizontal'} />
    );

    const containerRef = useRef(null);

    return (
        <div
            ref={containerRef}
            style={{
                flex: 1,
                display: 'flex',
                flexDirection: isVerticalSplit ? 'row' : 'column',
                overflow: 'hidden',
                background: T.bg,
                position: 'relative',
            }}
            data-faid="Workspace_Container_Operacoes"
        >
            {/* Área da tabela — DataTable montado UMA VEZ aqui, todos os modos
                a referenciam. Flex muda de "split" pra "full" sem desmontar. */}
            <div
                style={{
                    flex: isSplit ? `0 0 ${effectiveRatio}%` : '1 1 auto',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    minHeight: 0,
                }}
            >
                <DataTable {...tableProps} />
            </div>

            {/* Splitter + área do detail só pra modos split. */}
            {isSplit && (
                <>
                    <Splitter
                        orientation={isVerticalSplit ? 'vertical' : 'horizontal'}
                        ratio={effectiveRatio}
                        onChange={onRatioChange}
                        containerRef={containerRef}
                    />
                    <div
                        style={{
                            flex: '1 1 0',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            minWidth: isVerticalSplit ? MIN_DETAIL_PX : 0,
                            minHeight: isVerticalSplit ? 0 : 200,
                        }}
                    >
                        {detailContent}
                    </div>
                </>
            )}

            {/* Overlays absolutos pros modos modal/list/overlay — DataTable
                continua montado no fluxo normal, este overlay só cobre por cima. */}
            {isOverlayLike && selected && (
                <>
                    <button
                        type="button"
                        aria-label="Fechar painel"
                        onClick={() => onSelect(null)}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            background:
                                effectiveMode === 'modal'
                                    ? 'rgba(0,0,0,0.65)'
                                    : effectiveMode === 'overlay'
                                      ? 'rgba(0,0,0,0.15)'
                                      : 'transparent',
                            zIndex: 30,
                            border: 'none',
                            cursor: 'default',
                            padding: 0,
                        }}
                    />
                    <div
                        style={
                            effectiveMode === 'modal'
                                ? {
                                      position: 'absolute',
                                      top: '50%',
                                      left: '50%',
                                      transform: 'translate(-50%, -50%)',
                                      width: 520,
                                      maxWidth: 'calc(100% - 48px)',
                                      height: '80%',
                                      maxHeight: 680,
                                      background: T.bg,
                                      borderRadius: 10,
                                      boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
                                      zIndex: 70,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      overflow: 'hidden',
                                      animation: 'opModalIn 0.16s ease-out',
                                  }
                                : effectiveMode === 'list'
                                  ? {
                                        position: 'absolute',
                                        inset: 0,
                                        background: T.bg,
                                        zIndex: 50,
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }
                                  : {
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        bottom: 0,
                                        width: `${100 - overlayRatio}%`,
                                        minWidth: MIN_DETAIL_PX,
                                        background: T.bg,
                                        zIndex: 40,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        boxShadow: '-8px 0 32px rgba(0,0,0,0.12)',
                                        animation: 'slideIn 0.18s ease-out',
                                    }
                        }
                    >
                        <DetailPanel {...detailProps} />
                    </div>
                </>
            )}
        </div>
    );
}
